import { useEffect, useState, useRef } from "react";

import moment from "moment";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Box, Checkbox, TextField, IconButton, InputAdornment, Menu, MenuItem } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EmailIcon from "@mui/icons-material/Email";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PhoneIcon from "@mui/icons-material/Phone";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

/* eslint-disable import/no-extraneous-dependencies */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faViber, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
/* eslint-enable import/no-extraneous-dependencies */

import { createApplication } from "../../../redux/application/actions";
import { getUsers } from "../../../redux/users/actions";
import { capitalizeFirstLetter, capitalizeName } from "../../../utilities";
import Table from "../../../components/shared/Table";
import PrimaryButton from "../../../components/shared/PrimaryButton";
import SecondaryButton from "../../../components/shared/SecondaryButton";
import { getCurrentUserSelector } from "../../../redux/users/selector";
import { Roles } from "../../../utilities/constants";
import Filter from "../../../components/shared/Filter";
import { filterFormFields, filterFormSchema } from "./components/filterDetails";
import { setPreviousPath } from "../../../redux/utilities/actions";

export const ViewButton = ({ id, onClick }) => (
  // eslint-disable-next-line react/destructuring-assignment
  <Link to={`/users/${id}`}>
    <PrimaryButton onClick={onClick}>View</PrimaryButton>
  </Link>
);

ViewButton.propTypes = {
  id: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
};

const CustomCheckbox = ({ checked, onClick, disabled }) => <Checkbox disabled={disabled} checked={checked} onClick={onClick} />;

CustomCheckbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const PhoneNumberWithMenu = ({ phone = null }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWhatsApp = () => {
    window.location.href = `whatsapp://send?phone=${phone}`;
    handleClose();
  };

  const handleViber = () => {
    window.open(`viber://chat?number=${phone}`, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const handleCall = () => {
    window.location.href = `tel:${phone}`;
    handleClose();
  };

  if (!phone) return "-";

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
      <span>{phone}</span>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{
          padding: '2px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <ArrowDropDownIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleWhatsApp}>
          <FontAwesomeIcon icon={faWhatsapp} style={{ marginRight: '8px' }} />
          WhatsApp
        </MenuItem>
        <MenuItem onClick={handleViber}>
          <FontAwesomeIcon icon={faViber} style={{ marginRight: '8px' }} />
          Viber
        </MenuItem>
        <MenuItem onClick={handleCall}>
          <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
          Call
        </MenuItem>
      </Menu>
    </Box>
  );
};

PhoneNumberWithMenu.propTypes = {
  phone: PropTypes.string,
};

PhoneNumberWithMenu.defaultProps = {
  phone: null,
};

const columns = (dispatch, promoters, selectedRecords, handleSelectRecord, location) => [
  {
    id: "id",
    label: "ID",
    props: {
      align: "center",
    },
  },
  {
    id: "select",
    label: "Select",
    type: "button",
    value: (row) => (
      <Checkbox
        checked={selectedRecords.includes(row.id)}
        onChange={() => handleSelectRecord(row.id)}
      />
    ),
    props: {
      align: "center",
    },
  },
  {
    id: "fullName",
    label: "Name",
    value: (row) => {
      const firstName = capitalizeName(row?.firstName) || "";
      const lastName = capitalizeName(row?.lastName) || "";
      return `${firstName} ${lastName}`.trim() || "-";
    },
    props: {
      align: "center",
    },
  },
  {
    id: "email",
    label: "E-mail",
    value: (row) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        <Link 
          to={`/users/${row.id}`} 
          style={{ 
            color: 'inherit', 
            textDecoration: 'none',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={e => { e.target.style.color = '#d32f2f'; }}
          onMouseLeave={e => { e.target.style.color = 'inherit'; }}
          onClick={() => dispatch(setPreviousPath(`/users${location.search}`))}
        >
          {row?.email}
        </Link>
        {row?.email && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `mailto:${row.email}`;
            }}
            sx={{
              padding: '4px',
              '&:hover': {
                color: '#d32f2f',
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
          >
            <EmailIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    ),
    props: {
      align: "center",
    },
  },
  {
    id: "mobile",
    label: "Mobile Number",
    value: (row) => <PhoneNumberWithMenu phone={row?.profile?.phone} />,
    props: {
      align: "center",
    },
  },
  {
    id: "createdAt",
    label: "Registration Date",
    value: (row) => capitalizeFirstLetter(moment(row.createdAt).format("DD/MM/YYYY")),
    props: {
      align: "center",
    },
  },
];

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector(getCurrentUserSelector);
  const users = useSelector((state) => state.users.data || []);
  const promoters = useSelector((state) => state.users.data.filter((user) => user.role === Roles.PROMOTER.value));
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [createdAtStartDate, setCreatedAtStartDate] = useState("");
  const [createdAtEndDate, setCreatedAtEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const filterRef = useRef();
  const [assignJob, setAssignJob] = useState(() => {
    const stateJob = location.state?.assignJob;
    if (stateJob) {
      return stateJob;
    }
    const params = new URLSearchParams(location.search);
    const jobId = params.get("assignJobId") || params.get("assignJob");
    if (jobId) {
      return {
        id: Number(jobId),
        title: params.get("assignJobTitle") || "",
      };
    }
    return null;
  });
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (currentUser.role !== Roles.ADMIN.value) {
      navigate("/jobs");
    }
  }, [currentUser]);

  useEffect(() => {
    if (location.state?.assignJob) {
      setAssignJob(location.state.assignJob);
      return;
    }
    const params = new URLSearchParams(location.search);
    const jobId = params.get("assignJobId") || params.get("assignJob");
    if (jobId) {
      setAssignJob({
        id: Number(jobId),
        title: params.get("assignJobTitle") || "",
      });
    }
  }, [location.state, location.search]);

  useEffect(() => {
    dispatch(getUsers());
  }, []);

  // Apply date filtering and search whenever filters change
  useEffect(() => {
    if (!users || users.length === 0) return;

    let filtered = users;

    // Apply date filtering
    if (createdAtStartDate) {
      const startDate = new Date(createdAtStartDate);
      filtered = filtered.filter(user => new Date(user.createdAt) >= startDate);
    }

    if (createdAtEndDate) {
      const endDate = new Date(createdAtEndDate);
      filtered = filtered.filter(user => new Date(user.createdAt) <= endDate);
    }

    // Apply search filtering
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user => {
        // Helper function to check if array contains search term
        const arrayIncludes = (arr) => {
          if (!arr) return false;
          if (Array.isArray(arr)) {
            return arr.some(item => {
              if (typeof item === 'string') {
                return item.toLowerCase().includes(searchLower);
              }
              if (typeof item === 'object' && item !== null) {
                return Object.values(item).some(val => 
                  typeof val === 'string' && val.toLowerCase().includes(searchLower)
                );
              }
              return false;
            });
          }
          return typeof arr === 'string' && arr.toLowerCase().includes(searchLower);
        };

        return (
          // Basic fields
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.id?.toString().includes(searchLower) ||
          user.role?.toLowerCase().includes(searchLower) ||
          user.referral?.toLowerCase().includes(searchLower) ||
          // Profile fields
          user.profile?.firstName?.toLowerCase().includes(searchLower) ||
          user.profile?.lastName?.toLowerCase().includes(searchLower) ||
          user.profile?.nationality?.toLowerCase().includes(searchLower) ||
          user.profile?.address?.country?.toLowerCase().includes(searchLower) ||
          user.profile?.address?.city?.toLowerCase().includes(searchLower) ||
          // Array fields
          arrayIncludes(user.profile?.languages) ||
          arrayIncludes(user.profile?.workExperience) ||
          arrayIncludes(user.profile?.education)
        );
      });
    }

    setFilteredUsers(filtered);
  }, [users, createdAtStartDate, createdAtEndDate, searchTerm]);

  const handleSelectRecord = (id) => {
    setSelectedRecords((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((recordId) => recordId !== id)
        : [...prevSelected, id]
    );
  };

  const exportToCSV = () => {
    const selectedData = filteredUsers.filter((user) => selectedRecords.includes(user.id));
    const csvContent = [
      ['ID', 'Name', 'E-mail', 'Role', 'Registration Date'],
      ...selectedData.map((user) => [
        user.id,
        `${user.firstName || ""} ${user.lastName || ""}`.trim() || "-",
        user.email,
        user.role,
        moment(user.createdAt).format('DD/MM/YYYY'),
      ]),
    ]
      .map((e) => e.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'selected_users.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAssign = () => {
    if (!assignJob) {
      toast.error("Unable to determine job for assignment.");
      return;
    }

    setIsAssigning(true);
    Promise.all(
      selectedRecords.map((userId) =>
        dispatch(createApplication({ jobId: assignJob.id, userId })).unwrap()
      )
    )
      .then(() => {
        toast.success(
          assignJob.title
            ? `Selected users have been assigned to ${assignJob.title}.`
            : "Selected users have been assigned to the job."
        );
        setSelectedRecords([]);
      })
      .catch((error) => {
        if (Array.isArray(error)) {
          toast.error(error.filter(Boolean).join(", "));
        } else if (typeof error === "string") {
          toast.error(error);
        } else if (error?.message) {
          toast.error(error.message);
        } else {
          toast.error("Failed to assign one or more users. Please try again.");
        }
      })
      .finally(() => {
        setIsAssigning(false);
      });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Single row with date filters and buttons - aligned with table */}
      <Box sx={{ 
        width: "95%", 
        margin: "auto", 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', md: 'center' }, 
        gap: '10px', 
        marginBottom: '10px' 
      }}>
        {/* Date filters on the left */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center', 
          gap: '10px',
          width: { xs: '100%', md: 'auto' }
        }}>
          <TextField
            label="Registered From"
            type="date"
            size="small"
            value={createdAtStartDate}
            onChange={(e) => setCreatedAtStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: '180px' } }}
          />
          <TextField
            label="Registered To"
            type="date"
            size="small"
            value={createdAtEndDate}
            onChange={(e) => setCreatedAtEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: '180px' } }}
          />
          <IconButton 
            onClick={() => {
              setCreatedAtStartDate("");
              setCreatedAtEndDate("");
              setSearchTerm("");
            }}
            disabled={!createdAtStartDate && !createdAtEndDate && !searchTerm}
            sx={{
              borderRadius: '4px',
              alignSelf: { xs: 'center', sm: 'auto' },
              '&:hover': {
                borderBottom: '2px solid #C02026',
                backgroundColor: 'transparent',
              },
            }}
          >
            <ClearIcon sx={{ color: '#C02026' }} />
          </IconButton>
          <TextField
            label="General search"
            type="text"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, city..."
            sx={{ width: { xs: '100%', sm: '280px' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: '#C02026' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {/* Buttons on the right */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center', 
          gap: '10px',
          width: { xs: '100%', md: 'auto' }
        }}>
          {assignJob && selectedRecords.length > 0 && (
            <PrimaryButton
              onClick={handleAssign}
              disabled={isAssigning}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {isAssigning ? "Assigning..." : "Assign"}
            </PrimaryButton>
          )}
          <SecondaryButton 
            onClick={toggleFilters}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {showFilters ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </SecondaryButton>
          <PrimaryButton
            onClick={exportToCSV}
            disabled={selectedRecords.length === 0}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <FileDownloadIcon />
          </PrimaryButton>
        </Box>
      </Box>
      
      {/* Filter component with hidden internal button - always rendered for performance */}
      <Box sx={{ display: showFilters ? 'block' : 'none' }}>
        {users && (
          <Filter 
            ref={filterRef}
            items={users} 
            setFilteredItems={setFilteredUsers} 
            filterSteps={filterFormFields(promoters)} 
            filterSchema={filterFormSchema}
            hideInternalButton
            shouldStartOpen
          />
        )}
      </Box>
      
      <Table columns={columns(dispatch, promoters, selectedRecords, handleSelectRecord, location)} data={filteredUsers || []} noResultsContent="There are no users." />
    </Box>
  );
};

export default Users;
